import { useState, useEffect, useMemo, memo } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Image,
  TouchableOpacity,
  Linking,
  TextInput,
} from "react-native";
import { useRouter } from "expo-router";
import { useAuth } from "../AuthContext";
import { useAcquisition } from "../AcquisitionContext";
import { LinearGradient } from "expo-linear-gradient";
import { SafeAreaView } from "react-native-safe-area-context";
import { useDevice, useScreenDimensions } from "../../_layout";
import { useTranslation } from "react-i18next";
import { Ionicons } from "@expo/vector-icons";
import AccessWithGoogle from "../components/AccessWithGoogle";
import LanguagePicker from "../components/LanguagePicker";
import CustomActivityIndicator from "../../Components/CustomActivityIndicator";
import NoTranslateText from "../../Components/NoTranslateText";
import BlackPng from "../../../assets/BlackPng.png";
import Colors from "../../../Resources/Color";
import CoachesLogos from "../../../assets/CoachesLogos.webp";
import {
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import { auth } from "../../Firestore/FirestoreConfig";

/**
 * Signup view component
 * Web-only signup page with gradient sidebar and main content area
 */
const SignupView = () => {
  const [isRestrictedBrowser, setIsRestrictedBrowser] = useState(false);

  const { signup } = useAuth();
  const { buildUrlWithParams } = useAcquisition();
  const router = useRouter();
  const { screenSize } = useScreenDimensions();
  const { t, i18n } = useTranslation();
  const { isMobile } = useDevice();

  // Form state - lifted to parent to persist across screenSize changes
  const [signInHovered, setSignInHovered] = useState(false);
  const [signupButtonHovered, setSignupButtonHovered] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Detect if user is on restricted in-app browsers where Google Sign-In doesn't work
  useEffect(() => {
    if (typeof navigator !== "undefined") {
      const userAgent = navigator.userAgent;
      const restricted =
        userAgent.includes("Instagram") || // Instagram in-app browser
        userAgent.includes("FBAN") || // Facebook App (iOS)
        userAgent.includes("FBAV") || // Facebook App (Android)
        userAgent.includes("FB_IAB") || // Facebook In-App Browser
        userAgent.includes("musical_ly") || // TikTok
        userAgent.includes("Snapchat") || // Snapchat in-app browser
        userAgent.includes("Line") || // LINE in-app browser
        userAgent.includes("LinkedIn") || // LinkedIn in-app browser
        userAgent.includes("Twitter") || // Twitter in-app browser (X)
        userAgent.includes("Telegram") || // Telegram in-app browser
        userAgent.includes("WhatsApp") || // WhatsApp in-app browser
        userAgent.includes("Pinterest") || // Pinterest in-app browser
        userAgent.includes("Reddit"); // Reddit in-app browser
      setIsRestrictedBrowser(restricted);
    }
  }, []);

  // Skeleton card component - static, no animation
  const SkeletonCard = ({ style, type }) => {
    if (type === "first") {
      return (
        <View style={[styles.skeletonCard, style]}>
          <View
            style={[
              styles.skeletonLine,
              { width: "60%", backgroundColor: "rgba(227, 225, 225, 0.4)" },
            ]}
          />
          <View style={styles.skeletonCircle} />
          <View style={[styles.skeletonLine, { width: "100%" }]} />
        </View>
      );
    }

    if (type === "second") {
      return (
        <View style={[styles.skeletonCard, style]}>
          {/* First row */}
          <View style={styles.skeletonRow}>
            <View
              style={[
                styles.skeletonDot,
                { backgroundColor: "rgba(227, 225, 225, 0.4)" },
              ]}
            />
            <View style={[styles.skeletonLine, { width: "auto", flex: 1 }]} />
            <View style={[styles.skeletonLine, { width: "auto", flex: 3 }]} />
            <View style={[styles.skeletonLine, { width: "auto", flex: 1 }]} />
          </View>

          {/* Separator */}
          <View style={styles.skeletonSeparator} />

          {/* Second row (same as first) */}
          <View style={styles.skeletonRow}>
            <View
              style={[
                styles.skeletonDot,
                { backgroundColor: "rgba(227, 225, 225, 0.4)" },
              ]}
            />
            <View style={[styles.skeletonLine, { width: "auto", flex: 1 }]} />
            <View style={[styles.skeletonLine, { width: "auto", flex: 3 }]} />
            <View style={[styles.skeletonLine, { width: "auto", flex: 1 }]} />
          </View>
        </View>
      );
    }

    if (type === "third") {
      return (
        <View style={[styles.skeletonCard, style]}>
          <View style={styles.skeletonRow}>
            <View style={[styles.skeletonLine, { width: "auto", flex: 1 }]} />
            <View style={[styles.skeletonLine, { width: "auto", flex: 1 }]} />
            <View style={[styles.skeletonLine, { width: "auto", flex: 1 }]} />
          </View>
          <View style={styles.skeletonRow}>
            <View style={[styles.skeletonLine, { width: "auto", flex: 1 }]} />
            <View style={[styles.skeletonLine, { width: "auto", flex: 1 }]} />
            <View style={[styles.skeletonLine, { width: "auto", flex: 1 }]} />
          </View>
          <View
            style={[
              styles.skeletonLine,
              {
                width: "100%",
                backgroundColor: "rgba(227, 225, 225, 0.4)",
                marginTop: 4,
              },
            ]}
          />
        </View>
      );
    }

    return null;
  };

  // Google sign in handler
  const [googleLoading, setGoogleLoading] = useState(false);

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    setGoogleLoading(true);

    await signInWithPopup(auth, provider)
      .then((result) => {
        const credential = GoogleAuthProvider.credentialFromResult(result);
        const token = credential.accessToken;
        const user = result.user;
        setGoogleLoading(false);
      })
      .catch((error) => {
        console.error("Google Sign-in Error:", error);

        const errorCode = error.code;
        const errorMessage = error.message;
        const email = error.customData?.email || "No email available"; // Safe access
        const credential = GoogleAuthProvider.credentialFromError(error);

        console.log("Error details:", {
          errorCode,
          errorMessage,
          email,
          credential,
        });
        setGoogleLoading(false);
      });
  };

  // Terms and Conditions component
  const TermsAndConditions = () => {
    const [termsHovered, setTermsHovered] = useState(false);
    const [privacyHovered, setPrivacyHovered] = useState(false);

    return (
      <>
        <Text style={styles.termsText}>
          {t("byContinuingYouAgree-string")}{" "}
        </Text>
        <TouchableOpacity
          onPress={() =>
            Linking.openURL(
              i18n.language === "es"
                ? "https://www.efortcoach.com/es/terms-and-conditions"
                : "https://www.efortcoach.com/terms-and-conditions"
            ).catch((err) => console.error("Couldn't load page", err))
          }
          onMouseEnter={() => !isMobile && setTermsHovered(true)}
          onMouseLeave={() => setTermsHovered(false)}
        >
          <Text
            style={[
              styles.termsLink,
              termsHovered && { color: "rgba(138, 138, 138, 1)" },
            ]}
          >
            {t("termsAndConditions-string")}
          </Text>
        </TouchableOpacity>
        <Text style={styles.termsText}> {t("and-string")} </Text>
        <TouchableOpacity
          onPress={() =>
            Linking.openURL(
              i18n.language === "es"
                ? "https://www.efortcoach.com/es/privacy-policy"
                : "https://www.efortcoach.com/privacy-policy"
            ).catch((err) => console.error("Couldn't load page", err))
          }
          onMouseEnter={() => !isMobile && setPrivacyHovered(true)}
          onMouseLeave={() => setPrivacyHovered(false)}
        >
          <Text
            style={[
              styles.termsLink,
              privacyHovered && { color: "rgba(138, 138, 138, 1)" },
            ]}
          >
            {t("privacyPolicy-string")}
          </Text>
        </TouchableOpacity>
      </>
    );
  };

  // Left gradient sidebar - memoized to prevent unnecessary re-renders
  const LeftSidebar = useMemo(
    () => (
      <LinearGradient
        colors={["#204756", "#204756", "#306A7C"]}
        locations={[0, 0.7, 1]}
        style={styles.sidebar}
      >
        <View style={styles.sidebarContent}>
          {/* Header text */}
          <Text style={styles.headerText}>
            {t("replaceSpreadsheets-string")}
          </Text>

          {/* Three skeleton cards */}
          <View style={styles.cardsContainer}>
            <SkeletonCard style={styles.leftCard} type="first" />
            <SkeletonCard style={styles.centerCard} type="second" />
            <SkeletonCard style={styles.rightCard} type="third" />
          </View>

          {/* Trusted by section */}
          <View style={styles.trustedSection}>
            <Text style={styles.trustedText}>
              {t("trustedByCoaches-string")}
            </Text>
            <View style={styles.logosContainer}>
              <Image
                source={CoachesLogos}
                style={{ width: 300, height: 60 }}
                resizeMode="contain"
              />
            </View>
          </View>
        </View>
      </LinearGradient>
    ),
    [t]
  );

  // Email validation regex
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSignup = () => {
    setError(""); // Clear previous errors

    const trimmedEmail = email.trim();

    // Validate email format
    if (trimmedEmail === "") {
      setError(t("emailRequired-string") || "Email is required");
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setError(t("invalidEmail-string") || "Invalid email format");
      return;
    }

    // Validate password
    if (password.length < 6) {
      setError(
        t("passwordMinLength-string") ||
          "Password must be at least 6 characters"
      );
      return;
    }

    setIsLoading(true);

    createUserWithEmailAndPassword(auth, trimmedEmail, password)
      .then((userCredential) => {
        const user = userCredential.user;
        console.log("User created with:", user.email);
        setError(""); // Clear error on success
        setIsLoading(false);
      })
      .catch((error) => {
        // Parse Firebase error messages
        let errorMessage = error.message;
        if (error.code === "auth/email-already-in-use") {
          errorMessage =
            t("emailAlreadyUser-string") || "Email is already in use";
        } else if (error.code === "auth/invalid-email") {
          errorMessage = t("invalidEmail-string") || "Invalid email format";
        } else if (error.code === "auth/weak-password") {
          errorMessage =
            t("passwordMinLength-string") || "Password is too weak";
        }
        setError(errorMessage);
        setIsLoading(false);
        console.error("Signup error:", error);
      });
  };

  // Main content area (right side)
  const MainContent = useMemo(() => {
    return (
      <View style={styles.mainContent}>
        <ScrollView
          contentContainerStyle={[
            styles.scrollContent,
            {
              paddingHorizontal: screenSize === "small" ? 12 : 24,
            },
          ]}
        >
          <View style={styles.contentContainer}>
            {/* Logo */}
            <Image source={BlackPng} style={styles.logo} resizeMode="contain" />

            {/* Heading */}
            <NoTranslateText style={styles.headingText}>
              {t("tryEfortCoachFree-string")}
            </NoTranslateText>

            {/* Subtext */}
            <Text style={styles.subtext}>{t("noCreditCardNeeded-string")}</Text>

            {/* Show email/password fields for restricted browsers, Google button otherwise */}
            {isRestrictedBrowser ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSignup();
                }}
                style={{ width: 280 }}
              >
                <View style={styles.emailPasswordContainer}>
                  <TextInput
                    style={[
                      styles.input,
                      emailFocused && styles.inputFocused,
                      screenSize === "small" && { fontSize: 16 },
                    ]}
                    placeholder={t("email-string") || "Email"}
                    placeholderTextColor={Colors.grayColor}
                    value={email}
                    onChangeText={(text) => {
                      setError("");
                      setEmail(text);
                    }}
                    onFocus={() => setEmailFocused(true)}
                    onBlur={() => setEmailFocused(false)}
                    onSubmitEditing={() => {
                      // Focus on password field when Enter is pressed
                      if (email) {
                        // Move to next field - password will be focused by default
                      }
                    }}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoComplete="email"
                    maxLength={254}
                    returnKeyType="next"
                  />
                  <View style={styles.passwordContainer}>
                    <TextInput
                      style={[
                        styles.input,
                        passwordFocused && styles.inputFocused,
                        screenSize === "small" && { fontSize: 16 },
                      ]}
                      placeholder={t("password-string") || "Password"}
                      placeholderTextColor={Colors.grayColor}
                      value={password}
                      onChangeText={(text) => {
                        setError("");
                        setPassword(text);
                      }}
                      onFocus={() => setPasswordFocused(true)}
                      onBlur={() => setPasswordFocused(false)}
                      onSubmitEditing={() => {
                        // Trigger signup when Enter is pressed in password field
                        if (email && password.length >= 6 && !isLoading) {
                          handleSignup();
                        }
                      }}
                      secureTextEntry={!showPassword}
                      autoCapitalize="none"
                      autoComplete="password"
                      maxLength={128}
                      returnKeyType="go"
                    />
                    <TouchableOpacity
                      style={styles.showPasswordButton}
                      onPress={() => setShowPassword(!showPassword)}
                      onMouseEnter={(e) => {
                        if (!isMobile) e.currentTarget.style.opacity = "0.7";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = "1";
                      }}
                    >
                      <Ionicons
                        name={showPassword ? "eye-off-outline" : "eye-outline"}
                        size={18}
                        color={Colors.grayColor}
                      />
                    </TouchableOpacity>
                  </View>

                  {/* Error message */}
                  {error != "" && <Text style={styles.errorText}>{error}</Text>}

                  <TouchableOpacity
                    style={[
                      styles.signupButton,
                      (!email || password.length < 6) &&
                        styles.signupButtonDisabled,
                      signupButtonHovered && styles.signupButtonHovered,
                    ]}
                    onPress={handleSignup}
                    onMouseEnter={() =>
                      !isMobile && setSignupButtonHovered(true)
                    }
                    onMouseLeave={() => setSignupButtonHovered(false)}
                    disabled={!email || password.length < 6 || isLoading}
                  >
                    {isLoading ? (
                      <CustomActivityIndicator large={false} white={true} />
                    ) : (
                      <Text style={styles.signupButtonText}>
                        {t("signUp-string") || "Sign Up"}
                      </Text>
                    )}
                  </TouchableOpacity>
                </View>
              </form>
            ) : (
              <AccessWithGoogle
                mode="signup"
                language={i18n.language}
                onPress={signInWithGoogle}
                width={280}
                isLoading={googleLoading}
                setIsLoading={setGoogleLoading}
              />
            )}

            {/* Sign in link */}
            <View style={styles.signInContainer}>
              <Text style={styles.signInText}>
                {t("alreadyHaveAccount-string")}{" "}
              </Text>
              <TouchableOpacity
                onPress={() => router.replace(buildUrlWithParams("/login"))}
                onMouseEnter={() => !isMobile && setSignInHovered(true)}
                onMouseLeave={() => setSignInHovered(false)}
              >
                <Text
                  style={[
                    styles.signInLink,
                    signInHovered && { color: "#2E5E6E" },
                  ]}
                >
                  {t("signIn-string")}
                </Text>
              </TouchableOpacity>
            </View>

            {/* Terms and conditions - only show on small screens */}
            {screenSize != "large" && (
              <View style={styles.termsContainerInline}>
                <TermsAndConditions />
              </View>
            )}
          </View>
        </ScrollView>

        {/* Terms and conditions - absolute positioned for larger screens */}
        {screenSize === "large" && (
          <View style={styles.termsContainer}>
            <View style={styles.termsInnerContainer}>
              <TermsAndConditions />
            </View>
          </View>
        )}

        {/* Language Picker - bottom left */}
        <View style={styles.languagePickerContainer}>
          <LanguagePicker />
        </View>
      </View>
    );
  }, [
    screenSize,
    t,
    i18n.language,
    isRestrictedBrowser,
    googleLoading,
    email,
    password,
    emailFocused,
    passwordFocused,
    error,
    isLoading,
    signInHovered,
    signupButtonHovered,
    showPassword,
  ]);

  return (
    <SafeAreaView style={styles.container} edges={["top", "bottom"]}>
      {screenSize === "large" && LeftSidebar}
      {MainContent}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: "row",
    backgroundColor: "#FFFFFF",
    height: "100%",
  },
  sidebar: {
    width: 510,
    // flex: 1,
  },
  sidebarContent: {
    flex: 1,
    padding: 48,
    justifyContent: "space-between",
  },
  headerText: {
    fontSize: 24,
    fontWeight: "600",
    color: "#FFFFFF",
    lineHeight: 40,
    marginBottom: 32,
  },
  cardsContainer: {
    justifyContent: "space-between",
    alignItems: "center",
    gap: 24,
    marginBottom: 36,
  },
  skeletonCard: {
    backgroundColor: "rgba(255, 255, 255, 0.48)",
    gap: 8,
    borderRadius: 12,
    padding: 16,
  },
  leftCard: {
    alignSelf: "flex-start",
    width: "45%",
  },
  centerCard: {
    alignSelf: "stretch",
  },
  rightCard: {
    width: "55%",
    alignSelf: "flex-end",
  },
  skeletonHeader: {
    width: "100%",
    height: 40,
    backgroundColor: "rgba(227, 225, 225, 0.3)",

    borderRadius: 8,
    marginBottom: 12,
  },
  skeletonLine: {
    width: "100%",
    height: 10,
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    borderRadius: 6,
  },
  skeletonRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    width: "100%",
  },
  skeletonDot: {
    width: 10,
    height: 10,
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    borderRadius: 5,
  },
  skeletonSeparator: {
    width: "100%",
    height: 1,
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    marginVertical: 4,
  },
  skeletonCircle: {
    width: 24,
    height: 24,
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    borderRadius: 30,
  },
  skeletonSquare: {
    width: "100%",
    height: 80,
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    borderRadius: 8,
    marginTop: 8,
  },
  trustedSection: {
    marginTop: "auto",
    alignItems: "center",
  },
  trustedText: {
    fontSize: 14,
    fontWeight: "400",
    color: Colors.lightGrayColor,
    marginBottom: 16,
    opacity: 0.9,
  },
  logosContainer: {
    flexDirection: "row",
    gap: 16,
    flexWrap: "nowrap",
  },
  logoPlaceholder: {
    flex: 1,
    height: 80,
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    borderRadius: 8,
    justifyContent: "center",
    alignItems: "center",
  },
  logoText: {
    fontSize: 10,
    color: "#FFFFFF",
    opacity: 0.7,
  },
  mainContent: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 24,
  },
  contentContainer: {
    alignItems: "center",
    maxWidth: "100%",
  },
  logo: {
    width: 48,
    height: 48,
    marginBottom: 32,
  },
  headingText: {
    fontSize: 32,
    fontWeight: "600",
    color: "#000000",
    marginBottom: 16,
    textAlign: "center",
  },
  subtext: {
    fontSize: 14,
    fontWeight: "400",
    color: Colors.blackColor,
    marginBottom: 40,
    textAlign: "center",
  },
  signInContainer: {
    flexDirection: "row",
    marginTop: 16,
    alignItems: "center",
  },
  signInText: {
    fontSize: 14,
    color: Colors.grayColor,
  },
  signInLink: {
    fontSize: 14,
    color: Colors.bubbleColor,
  },
  termsContainer: {
    position: "absolute",
    bottom: 64,
    left: 0,
    right: 0,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 16,
  },
  termsInnerContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
    maxWidth: 320,
  },
  termsContainerInline: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 40,
    justifyContent: "center",
    alignItems: "center",
    maxWidth: 320,
  },
  termsText: {
    fontSize: 14,
    color: Colors.grayColor,
    textAlign: "center",
  },
  termsLink: {
    fontSize: 14,
    color: Colors.grayColor,
    fontWeight: "400",
    textDecorationLine: "underline",
  },
  languagePickerContainer: {
    position: "absolute",
    bottom: 12,
    left: 12,
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    borderRadius: 10,
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  emailPasswordContainer: {
    width: 280,
    gap: 16,
  },
  passwordContainer: {
    position: "relative",
    width: "100%",
  },
  showPasswordButton: {
    position: "absolute",
    right: 12,
    top: 0,
    bottom: 0,
    paddingHorizontal: 4,
    paddingVertical: 2,
    alignItems: "center",
    justifyContent: "center",
  },
  input: {
    width: "100%",
    height: 44,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: Colors.borderColor,
    borderRadius: 14,
    paddingHorizontal: 16,
    fontSize: 14,
    color: Colors.blackColor,
    outlineStyle: "none",
  },
  inputFocused: {
    borderColor: Colors.bubbleColor,
  },
  passwordCheckmark: {
    position: "absolute",
    right: 16,
    top: 12,
    fontSize: 18,
    color: Colors.bubbleColor,
    fontWeight: "bold",
  },
  errorText: {
    fontSize: 13,
    color: Colors.redColor,
    textAlign: "center",
    marginTop: -8,
  },
  signupButton: {
    width: "100%",
    height: 44,
    backgroundColor: Colors.bubbleColor,
    borderRadius: 14,
    justifyContent: "center",
    alignItems: "center",
  },
  signupButtonDisabled: {
    opacity: 0.5,
  },
  signupButtonHovered: {
    // opacity: 0.8,
    backgroundColor: "#2E5E6E",
  },
  signupButtonText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#FFFFFF",
  },
  fallbackContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
  },
});

export default SignupView;
